import { CAMERA_MODES, FocusSchema, IdSchema, LocaleSchema, ProviderDescriptorSchema, TaskSchema, VoiceInfoSchema, } from "@uzverse/core";
import { z } from "zod";
import { CompanyBrandingSchema } from "./branding.js";
/** REST prefix of the gateway API; identity routes are mounted under `/identity`. */
export const GATEWAY_API_PREFIX = "/api/v1";
export const IDENTITY_PREFIX = "/identity";
/**
 * Every HTTP route a browser client calls on the gateway (the single web ↔ gateway contract).
 * Sessions are the HttpOnly `uz_session` cookie (or `Authorization: Bearer` for non-browser
 * clients); the realtime WebSocket is `/ws` and authenticates with `{ type: "auth", token: "cookie" }`.
 */
export const GATEWAY_ROUTES = {
    // ---- identity (public) ----
    login: "/identity/auth/login",
    mfa: "/identity/auth/mfa",
    inviteLookup: "/identity/invites/lookup",
    inviteAccept: "/identity/invites/accept",
    // ---- identity (signed in) ----
    logout: "/identity/auth/logout",
    /** POST `{ currentPassword, newPassword }`; also answers a session that must change its password. */
    changePassword: "/identity/auth/password",
    totpEnroll: "/identity/auth/totp/enroll",
    totpConfirm: "/identity/auth/totp/confirm",
    /** POST `{ password, code }`: replaces the recovery codes (returned once). Not for the Owner. */
    recoveryCodes: "/identity/auth/recovery-codes",
    /** POST: invite a person (returns the invite token once). */
    invites: "/identity/invites",
    // ---- gateway ----
    me: "/api/v1/me",
    /** GET | PATCH: user preferences (locale, assistant name). */
    prefs: "/api/v1/settings",
    /** GET: the user's tasks (chat history), newest first. POST: submit a task. */
    tasks: "/api/v1/companies/:companyId/tasks",
    task: "/api/v1/companies/:companyId/tasks/:taskId",
    approvalDecision: "/api/v1/companies/:companyId/approvals/:approvalId/decision",
    notifications: "/api/v1/notifications",
    notificationsRead: "/api/v1/notifications/read",
    /** GET | PUT: assistant settings (name, appearance, voice, proactivity). */
    assistant: "/api/v1/assistant",
    /** GET: voice providers and voices for the user. */
    voices: "/api/v1/voices",
    /** POST `{ providerId, voiceId }`: a short sample (`voice.preview`). */
    voicePreview: "/api/v1/voices/preview",
    /** GET: what the assistant learned about the user. */
    memory: "/api/v1/memory",
    /** PATCH `{ text }` corrects, DELETE forgets (privacy deletion). */
    memoryItem: "/api/v1/memory/:id",
    /** GET: module settings in serializable form (user scope, plus company scope for owners). */
    moduleSettings: "/api/v1/module-settings",
    /** PUT `{ value }` sets, DELETE resets to the default. */
    moduleSetting: "/api/v1/module-settings/:key",
    /** GET: selectable models (friendly labels) and effort levels (`ModelCatalogResponse`). */
    models: "/api/v1/models",
    /** GET: whether the server voice path (TTS/STT) is up. */
    voiceStatus: "/api/v1/voice/status",
    /** POST `{ text, voiceId?, language? }` → raw `pcm_s16le` mono, rate in `x-sample-rate`. */
    voiceTts: "/api/v1/voice/tts",
    /** WebSocket: first frame `{type:"auth",token}`, then PCM16 16 kHz frames, `{type:"end"}`. */
    voiceStt: "/api/v1/voice/stt",
};
/** Fills `:param` placeholders (URI-encoded). */
export function fillRoute(template, params = {}) {
    return template.replace(/:(\w+)/g, (_m, name) => {
        const v = params[name];
        if (v === undefined)
            throw new Error(`missing route parameter ${name}`);
        return encodeURIComponent(v);
    });
}
// ---------------------------------------------------------------- identity bodies
export const LoginResponseSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("ok") }).passthrough(),
    z.object({
        status: z.literal("mfa_required"),
        challengeToken: z.string().min(1),
        expiresAt: z.string(),
    }),
]);
export const InviteLookupResponseSchema = z
    .object({
    email: z.string(),
    expiresAt: z.string(),
    invitedByName: z.string().optional(),
    disclosureVersion: z.string().optional(),
})
    .passthrough();
export const TotpEnrollResponseSchema = z
    .object({ secret: z.string(), otpauthUri: z.string() })
    .passthrough();
/** Confirming an enrollment returns the recovery codes exactly once. */
export const TotpConfirmResponseSchema = z
    .object({ recoveryCodes: z.array(z.string()).min(1) })
    .passthrough();
export const RecoveryCodesResponseSchema = TotpConfirmResponseSchema;
// ---------------------------------------------------------------- gateway bodies
/** The sky's hour: the viewer's clock (`auto`) or a fixed hour 0..24. */
export const TimeOfDaySchema = z.union([z.literal("auto"), z.number().min(0).max(24)]);
/** The UI theme: follow the OS, or force light or dark. */
export const ThemePrefSchema = z.enum(["system", "light", "dark"]);
/** World view preferences that follow the user across devices (localStorage is only a cache). */
export const WorldPrefsSchema = z.object({
    cameraMode: z.enum(CAMERA_MODES).optional(),
    quality: z.enum(["auto", "low", "high"]).optional(),
    dockOpen: z.boolean().optional(),
    /** `immersive`: the 3D view fills the page, panels are drawers. `docked`: the classic columns. */
    layout: z.enum(["immersive", "docked"]).optional(),
    /** Hour of day for the sky (0..24), or `auto` to follow the viewer's clock. */
    timeOfDay: TimeOfDaySchema.optional(),
});
/**
 * A user's character in the 3D world: a choice over the bundled realistic human models (not a
 * third-party GLB, so nothing is fetched from elsewhere). Every field is an index into a palette
 * the web app owns (`apps/web/src/world/humans/spec.ts`), so a spec stays valid when a palette
 * grows and out-of-range values are clamped on read.
 */
export const AVATAR_LIMITS = {
    skin: 8,
    hair: 6,
    hairColor: 8,
    outfit: 5,
    outfitColor: 10,
};
export const AvatarSpecSchema = z
    .object({
    body: z.enum(["male", "female"]),
    skin: z
        .number()
        .int()
        .min(0)
        .max(AVATAR_LIMITS.skin - 1),
    hair: z
        .number()
        .int()
        .min(0)
        .max(AVATAR_LIMITS.hair - 1),
    hairColor: z
        .number()
        .int()
        .min(0)
        .max(AVATAR_LIMITS.hairColor - 1),
    outfit: z
        .number()
        .int()
        .min(0)
        .max(AVATAR_LIMITS.outfit - 1),
    outfitColor: z
        .number()
        .int()
        .min(0)
        .max(AVATAR_LIMITS.outfitColor - 1),
})
    .strict();
export const UserPrefsSchema = z.object({
    locale: LocaleSchema.optional(),
    assistantName: z.string().optional(),
    avatar: AvatarSpecSchema.optional(),
    world: WorldPrefsSchema.optional(),
    theme: ThemePrefSchema.optional(),
});
export const CompanyRefSchema = z.object({
    id: IdSchema,
    name: z.string(),
    /** Public look (logo, colors): the browser tab icon and title follow the primary company. */
    branding: CompanyBrandingSchema.optional(),
    /** A demo company, listed after real ones. */
    demo: z.boolean().optional(),
});
export const MeResponseSchema = z.object({
    user: z.object({
        id: IdSchema,
        email: z.string(),
        displayName: z.string(),
        isOwner: z.boolean(),
        /** The password must be changed before anything else. */
        mustChangePassword: z.boolean().optional(),
        mfaVerified: z.boolean(),
        locale: LocaleSchema,
    }),
    settings: UserPrefsSchema,
    /** The user's companies; the first one is primary (chat goes there). */
    companies: z.array(CompanyRefSchema),
});
export const SubmitTaskBodySchema = z.object({
    text: z.string().trim().min(1).max(8000),
    focus: FocusSchema.nullable().default(null),
    clientRequestId: z.string().min(1).max(128),
    locale: LocaleSchema.optional(),
});
export const TaskListResponseSchema = z.object({ tasks: z.array(TaskSchema) });
export const PROACTIVITY_LEVELS = ["off", "low", "medium", "high"];
export const ProactivityLevelSchema = z.enum(PROACTIVITY_LEVELS);
export const VoiceChoiceSchema = z.object({
    providerId: z.string().min(1),
    voiceId: z.string().min(1),
});
export const AssistantSettingsSchema = z.object({
    name: z.string().trim().min(1).max(60),
    /** Placeholder until avatars exist (Phase 2). */
    appearance: z.string().max(60),
    voice: VoiceChoiceSchema.nullable(),
    proactivity: ProactivityLevelSchema,
});
export const VoiceCatalogSchema = z.object({
    providers: z.array(ProviderDescriptorSchema),
    voices: z.array(VoiceInfoSchema),
});
export const VoicePreviewResponseSchema = z.object({
    /** Base64 audio for server voices; absent for browser voices (the client speaks `text`). */
    audioBase64: z.string().optional(),
    format: z.string().optional(),
    sampleRate: z.number().optional(),
    text: z.string().optional(),
    language: z.string().optional(),
});
export const MEMORY_KINDS = ["fact", "preference", "routine"];
export const MemoryKindSchema = z.enum(MEMORY_KINDS);
export const MemoryItemSchema = z.object({
    /** The profile key (stable id of the fact). */
    id: z.string().min(1),
    kind: MemoryKindSchema,
    text: z.string(),
    source: z.string().optional(),
    confidence: z.number().optional(),
    updatedAt: z.string(),
});
/** Kind of a profile key: `*.preference*` keys are preferences, `routine.*` routines. */
export function memoryKindOf(key) {
    if (/(^|\.)routine/.test(key))
        return "routine";
    if (/(^|\.)(preference|prefers|likes)/.test(key))
        return "preference";
    return "fact";
}
export const VoiceStatusSchema = z.object({
    available: z.boolean(),
    tts: z.boolean(),
    stt: z.boolean(),
});
export const TtsBodySchema = z.object({
    text: z.string().trim().min(1).max(5000),
    voiceId: z.string().min(1).optional(),
    language: z.enum(["tr", "en", "auto"]).optional(),
});
//# sourceMappingURL=gateway.js.map